import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import * as Sentry from "@sentry/react";
import { auth } from './firebase.js';
import LoginScreen from './components/LoginScreen';
import ErrorBoundary from './components/ErrorBoundary';

// Default web UI
const DashboardV3Sloane = React.lazy(() => import('./components/v3/DashboardV3Sloane'));
// Mobile — renders on screens narrower than 768px
const MobileDashboardV3 = React.lazy(() => import('./components/v3/MobileDashboardV3'));
// Family Settings Admin Screen
const FamilySettingsScreen = React.lazy(() => import('./components/v3/FamilySettingsScreen'));
// Dictation Preview Test Page
const DictationPreview = React.lazy(() => import('./components/DictationPreview'));

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
      if (u) {
        Sentry.setUser({ id: u.uid, email: u.email });
      } else {
        Sentry.setUser(null);
      }
    });
    return unsub;
  }, []);

  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#E8F5E9',
        color: '#3D8B6A',
        fontSize: '1rem',
        gap: '12px',
      }}>
        <div style={{
          width: '20px', height: '20px',
          border: '2px solid rgba(27,58,44,0.2)',
          borderTop: '2px solid #1B3A2C',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        Loading…
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (window.location.pathname === '/test-dictation') {
    return (
      <ErrorBoundary label="DictationPreview">
        <React.Suspense fallback={<div style={{color:'#1B3A2C', padding:'20px'}}>Loading…</div>}>
          <DictationPreview />
        </React.Suspense>
      </ErrorBoundary>
    );
  }

  if (!user) {
    return <LoginScreen />;
  }

  if (window.location.pathname === '/settings/family') {
    return (
      <ErrorBoundary label="FamilySettings">
        <React.Suspense fallback={<div style={{color:'#1B3A2C', padding:'20px'}}>Loading…</div>}>
          <FamilySettingsScreen user={user} />
        </React.Suspense>
      </ErrorBoundary>
    );
  }



  return (
    <ErrorBoundary label="Dashboard">
      {isMobile ? (
        <React.Suspense fallback={<div style={{color:'#1B3A2C', padding:'20px'}}>Loading…</div>}>
          <MobileDashboardV3 user={user} />
        </React.Suspense>
      ) : (
        <React.Suspense fallback={<div style={{color:'#1B3A2C', padding:'20px'}}>Loading…</div>}>
          <DashboardV3Sloane user={user} />
        </React.Suspense>
      )}
    </ErrorBoundary>
  );
}

export default App;
